import { useState, useCallback } from 'react';

export interface ErrorState {
  message: string;
  code?: string;
  details?: any;
  timestamp: Date;
}

export function useErrorHandler() {
  const [error, setError] = useState<ErrorState | null>(null);

  const handleError = useCallback((error: unknown, context?: string) => {
    console.error(`Error ${context ? `in ${context}` : ''}:`, error);
    
    let errorState: ErrorState;
    
    if (error instanceof Error) {
      errorState = {
        message: error.message,
        code: error.name,
        details: error.stack,
        timestamp: new Date()
      };
    } else if (typeof error === 'string') {
      errorState = {
        message: error,
        timestamp: new Date()
      };
    } else if (error && typeof error === 'object' && 'message' in error) {
      errorState = {
        message: (error as any).message || 'An unexpected error occurred',
        code: (error as any).code,
        details: error,
        timestamp: new Date()
      };
    } else {
      errorState = {
        message: 'An unexpected error occurred',
        details: error,
        timestamp: new Date()
      };
    }

    setError(errorState);
    
    // You could send to error reporting service here
    // Example: Sentry.captureException(error, { extra: { context } });
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const retry = useCallback((fn: () => void | Promise<void>) => {
    clearError();
    try {
      const result = fn();
      if (result instanceof Promise) {
        result.catch((error) => handleError(error, 'retry operation'));
      }
    } catch (error) {
      handleError(error, 'retry operation');
    }
  }, [clearError, handleError]);

  return {
    error,
    handleError,
    clearError,
    retry,
    hasError: error !== null
  };
}

// Higher-order function for wrapping async operations
export function withErrorHandling<T extends any[], R>(
  fn: (...args: T) => Promise<R>,
  onError?: (error: unknown) => void
) {
  return async (...args: T): Promise<R | null> => {
    try {
      return await fn(...args);
    } catch (error) {
      if (onError) {
        onError(error);
      } else {
        console.error('Unhandled error in async operation:', error);
      }
      return null;
    }
  };
}

// Hook for handling API errors specifically
export function useApiErrorHandler() {
  const { handleError, ...rest } = useErrorHandler();

  const handleApiError = useCallback((error: unknown, operation?: string) => {
    let message = 'An error occurred';
    
    if (error && typeof error === 'object' && 'message' in error) {
      message = (error as any).message;
    } else if (typeof error === 'string') {
      message = error;
    }

    // Add operation context to the message
    if (operation) {
      message = `Failed to ${operation}: ${message}`;
    }

    handleError({ message, details: error }, operation);
  }, [handleError]);

  return {
    ...rest,
    handleError: handleApiError
  };
}
